/* eslint-disable react/jsx-no-literals */
import { ApolloProvider, useApolloClient } from '@apollo/client'
import { ChakraProvider } from '@chakra-ui/react'
import { NextPage } from 'next'
import type { Session } from 'next-auth'
import { signIn, signOut, useSession } from 'next-auth/react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { FC, ReactElement, ReactNode, useEffect, useMemo } from 'react'
import { IntlProvider } from 'react-intl'
import { elementScrollIntoViewPolyfill } from 'seamless-scroll-polyfill'
import * as Sentry from '@sentry/nextjs'
import '@emotion/react'
import {
  PortalProvider,
  ScrollMarginProvider,
  theme,
  usePortal,
} from '@gaia-family/ds'
import { env } from '@gaia-family/lib'
import { makeEmotionApp } from '@gaia-family/next-emotion-ssr'

import { createBrowserApolloClient } from '@lib/apollo'

import { AppProvider } from '@components/AppProvider'
import { Layout, LayoutProps } from '@components/Layout'

import { Routes } from '@hooks/useRouting'
import { GlobalStyles } from '@gaia-family/ds-emotion'
import { DEFAULT_LOCALE, useLocalisedConfig } from '@hooks/useLocalisedConfig'
import { getLocalisedDictionary } from '@lib/i18n'

type PageProps = {
  // allow providing and accessing a session passed from server props
  session?: Session
  layout?: { blendNavigation?: string }
}

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
  pageProps: PageProps
}

type AppPropsWithLayout = AppProps<PageProps> & {
  Component: NextPageWithLayout
}

declare module 'next-auth' {
  /**
   * Returned by the `jwt` callback and `getToken`, when using JWT
   * sessions
   */
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Session {
    user: {
      firstName: string
      lastName: string
      id: string
      createdAt: string
      email: string
      hubspotToken?: string
    }
  }
}

const GaiaPage: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  // Use the layout defined at the page level, if available
  // See docs https://nextjs.org/docs/basic-features/layouts#per-page-layouts
  const getLayout =
    Component.getLayout ??
    ((page: ReactNode, layoutProps: LayoutProps) => (
      <Layout {...layoutProps}>{page}</Layout>
    ))

  const { data: session } = useSession()

  if (session) {
    Sentry.configureScope((scope) => {
      scope.setUser({ id: session?.user?.id })
    })
  }

  const { resetStore } = useApolloClient()

  const { PortalExit } = usePortal('app')

  const layoutProps = {
    onSignIn: () => {
      resetStore()
      return signIn('credentials', {
        callbackUrl: Routes.profile,
      })
    },
    onSignOut: async () => {
      await signOut({
        callbackUrl: Routes.signIn,
      })
      resetStore()
    },
    session: session ?? null,
    ...(pageProps.layout ?? {}),
  }

  return (
    <>
      {getLayout(
        // @ts-expect-error the type of NextPage does not seem to be working here
        <Component {...pageProps} />,
        layoutProps
      )}
      <PortalExit />
    </>
  )
}

const GaiaApp: FC<AppPropsWithLayout> = (props) => {
  const { asPath } = useRouter()
  const { locale } = useLocalisedConfig()
  const apolloClient = useMemo(() => createBrowserApolloClient(), [])
  const dictionary = useMemo(() => getLocalisedDictionary(locale), [locale])

  useEffect(() => {
    elementScrollIntoViewPolyfill()
  }, [])

  // get canonical url for improved seo
  // see: https://rishimohan.me/blog/nextjs-canonical-tag
  const canonicalUrl = `https://${env.NEXT_PUBLIC_URL}${
    asPath === '/' ? '' : asPath
  }`.split('?')[0]

  return (
    <ApolloProvider client={apolloClient}>
      <Script id="gtm" strategy="afterInteractive">{`
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer', '${env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');
    `}</Script>
      <IntlProvider
        locale={locale || DEFAULT_LOCALE}
        defaultLocale={DEFAULT_LOCALE}
        messages={dictionary}
      >
        <ChakraProvider theme={theme}>
          <ScrollMarginProvider>
            <PortalProvider>
              <GlobalStyles />
              <AppProvider session={props.pageProps?.session}>
                <Head>
                  <title>Gaia Family - The new way to pay for IVF</title>
                  <meta
                    name="description"
                    content="Personal insurance and payment plans for IVF in the UK"
                  />
                  <meta property="og:title" content="Gaia Family" />
                  <meta property="og:type" content="website" />
                  <meta
                    property="og:site_name"
                    content="Gaia Family - The new way to pay for IVF"
                  />
                  <meta
                    property="og:description"
                    content="Personal insurance and payment plans for IVF in the UK"
                  />
                  <meta
                    property="og:image"
                    content="https://gaiafamily.com/images/social-banner.png"
                  />
                  <meta property="og:image:width" content="2880px" />
                  <meta property="og:image:height" content="1620px" />
                  <meta property="og:url" content="https://gaiafamily.com" />
                  <meta name="twitter:card" content="summary_large_image" />
                  <meta name="twitter:site" content="@gaia__family" />
                  <meta
                    property="twitter:image"
                    content="https://gaiafamily.com/images/social-banner.png"
                  />
                  <link rel="canonical" href={canonicalUrl} />
                </Head>
                <GaiaPage {...props} />
              </AppProvider>
            </PortalProvider>
          </ScrollMarginProvider>
        </ChakraProvider>
      </IntlProvider>
    </ApolloProvider>
  )
}

// use it https://www.npmjs.com/package/@cprecioso/next-emotion-ssr
// until they update emotion documentation with optimal nextjs ssr
// note: because of peer dependnecy issues we have adopted a fork
export default makeEmotionApp(GaiaApp)
